/**
 * Gateway API 엔드포인트 단일 레지스트리.
 * - baseURL(env.VITE_API_URL)은 client에서 주입 — 여기엔 path만.
 * - 버전 prefix(/v1)를 한 곳에서 관리: v2 전환 시 API_VERSION만 교체.
 */
import { AUTH_ENDPOINTS } from '@platform/auth';
import { BILLING_ENDPOINTS } from '@platform/billing';
import { MEDIA_ENDPOINTS } from '@platform/media';

export const API_VERSION = '/v1';

export const API_ENDPOINTS = {
  /** auth-service — SSOT 는 `@platform/auth` (console-web 과 같은 세션을 쓰므로 경로가 갈라지면 안 된다). */
  auth: AUTH_ENDPOINTS,
  /** media-service — SSOT 는 `@platform/media`. */
  media: MEDIA_ENDPOINTS,
  /**
   * 게시판(board) — 공지/업데이트/이벤트 공통. core-service 의 board 모듈.
   * path 에 게시판 type(notices|updates|events)을 끼워 단일 서비스로 처리.
   */
  news: {
    list: (type: string) => `${API_VERSION}/core/board/${type}`,
    detail: (type: string, id: string | number) =>
      `${API_VERSION}/core/board/${type}/${id}`,
    create: (type: string) => `${API_VERSION}/core/board/${type}`,
    update: (type: string, id: string | number) =>
      `${API_VERSION}/core/board/${type}/${id}`,
    remove: (type: string, id: string | number) =>
      `${API_VERSION}/core/board/${type}/${id}`,
  },
  /**
   * 도움말(docs) — core-service 의 docs 모듈.
   *
   * 상세가 `/docs/:section/:slug` 가 아니라 쿼리인 것은 서버 사정이다:
   * Gateway public-paths 를 EXACT 두 줄(tree/article)로 좁히려고 그렇게 뒀다.
   * PREFIX 로 열면 초안이 포함된 `articles/:id` 까지 비로그인 공개가 된다.
   */
  docs: {
    /** 섹션+문서 트리 + 리다이렉트 표. 사이드바·검색·이전다음이 전부 여기서 나온다. */
    tree: `${API_VERSION}/core/docs/tree`,
    /** 문서 상세(본문 포함). section/slug 는 params 로 넘긴다. */
    article: `${API_VERSION}/core/docs/article`,
    /**
     * 초안까지 포함한 관리용 조회(ADMIN). 공개 경로와 **path 가 갈라져 있다** —
     * Gateway 는 public path 를 만나면 사용자 헤더를 심기 전에 통과시키므로,
     * 같은 경로에 로그인 여부로 분기를 걸면 초안이 영원히 안 보인다.
     */
    manageTree: `${API_VERSION}/core/docs/manage/tree`,
    manageArticle: `${API_VERSION}/core/docs/manage/article`,
    /** 편집용 단건(초안 포함, ADMIN) */
    adminDetail: (id: string) => `${API_VERSION}/core/docs/articles/${id}`,
    createDoc: `${API_VERSION}/core/docs/articles`,
    updateDoc: (id: string) => `${API_VERSION}/core/docs/articles/${id}`,
    removeDoc: (id: string) => `${API_VERSION}/core/docs/articles/${id}`,
    createSection: `${API_VERSION}/core/docs/sections`,
    updateSection: (id: string) => `${API_VERSION}/core/docs/sections/${id}`,
    removeSection: (id: string) => `${API_VERSION}/core/docs/sections/${id}`,
    reorder: `${API_VERSION}/core/docs/reorder`,
    /**
     * "이 문서가 도움이 되었나요?" 접수. **비로그인 공개 POST** 다 —
     * 도움말은 로그인 없이 읽으므로 인증을 걸면 대부분의 응답을 못 받는다.
     */
    feedback: `${API_VERSION}/core/docs/feedback`,
    /** '아니오' 사유 목록(ADMIN). 집계 숫자는 manage/tree 에 이미 실려 온다. */
    manageFeedback: `${API_VERSION}/core/docs/manage/feedback`,
  },
  /**
   * 품셈해설(pumsem) — core-service 의 pumsem 모듈.
   *
   * 상세가 `/pumsem/:division/:code` 가 아니라 쿼리인 것은 docs 와 같은 서버
   * 사정이다: Gateway public-paths 를 EXACT 다섯 줄로 좁히려고 그렇게 뒀다.
   * PREFIX 로 열면 미발행 판이 포함된 `manage/*` 까지 비로그인 공개가 된다.
   *
   * `edition` 을 생략하면 서버가 **최신 발행판**으로 답한다. 견적서 근거로 붙일
   * 링크는 반드시 `edition` 을 붙여야 한다 — 없으면 반기마다 내용이 바뀐다.
   */
  pumsem: {
    /** 발행된 개정판 목록. 첫 원소가 최신이다. */
    editions: `${API_VERSION}/core/pumsem/editions`,
    /** 부문 → 장 → 절 → 항 트리. 품 표는 실려 오지 않는다. */
    tree: `${API_VERSION}/core/pumsem/tree`,
    /** 항목 상세. division/code/edition 은 params 로 넘긴다. */
    item: `${API_VERSION}/core/pumsem/item`,
    /** 품셈명·코드·직종·자재명·[주] 본문까지 훑는 검색. */
    search: `${API_VERSION}/core/pumsem/search`,
    /** 직전 판 대비 비교. 임의 두 판 비교는 서버가 제공하지 않는다. */
    diff: `${API_VERSION}/core/pumsem/diff`,
    /** 미발행 판 포함(ADMIN). 공개 경로와 **path 가 갈라져 있다**(docs 와 같은 이유). */
    manageEditions: `${API_VERSION}/core/pumsem/manage/editions`,
    manageTree: `${API_VERSION}/core/pumsem/manage/tree`,
    manageItem: `${API_VERSION}/core/pumsem/manage/item`,
    /** 숨긴 판까지 훑는 검색. 뷰어의 관리자 오버레이가 쓴다. */
    manageSearch: `${API_VERSION}/core/pumsem/manage/search`,
    /** 전역 부문 목록. "이 판에 없는 부문" 을 그리려면 편성 목록만으로는 부족하다. */
    manageDivisions: `${API_VERSION}/core/pumsem/manage/divisions`,
    /** 이 판에 실린 부문 + 순서 + 이 판에서의 항목 수. */
    manageEditionDivisions: `${API_VERSION}/core/pumsem/manage/edition-divisions`,
    /**
     * 관리 경로의 식별자는 **전부 slug/code** 다. uuid 를 밖으로 내보내면
     * 관리 화면이 판을 두 이름(slug 와 uuid)으로 들고 다니게 된다.
     * 예외는 `revision` — 항목 스냅샷은 slug 축이 없다(판+부문+코드가 셋이라
     * 경로가 길어지고, 편집 링크는 트리가 이미 id 를 실어 준다).
     */
    createEdition: `${API_VERSION}/core/pumsem/editions`,
    updateEdition: (slug: string) =>
      `${API_VERSION}/core/pumsem/editions/${slug}`,
    /** 공개 / 숨김 토글(PATCH). 발행 취소가 아니라 가시성만 바꾼다. */
    editionVisibility: (slug: string) =>
      `${API_VERSION}/core/pumsem/editions/${slug}/visibility`,
    importEdition: (slug: string) =>
      `${API_VERSION}/core/pumsem/editions/${slug}/import`,
    /** 이 판에 부문 싣기(POST) / 빼기(DELETE, `?division=`). */
    editionDivisions: (slug: string) =>
      `${API_VERSION}/core/pumsem/editions/${slug}/divisions`,
    /** 이 판의 부문 순서(PATCH). 전체 배열을 통째로 보낸다. */
    editionDivisionOrder: (slug: string) =>
      `${API_VERSION}/core/pumsem/editions/${slug}/divisions/order`,
    createDivision: `${API_VERSION}/core/pumsem/divisions`,
    /** 수정(PUT) / 삭제(DELETE) — 같은 경로에 메서드만 다르다. */
    division: (slug: string) => `${API_VERSION}/core/pumsem/divisions/${slug}`,
    /** 편집용 단건 조회 / 수정 / 이 판에서 제외 — 같은 경로에 메서드만 다르다. */
    revision: (id: string) => `${API_VERSION}/core/pumsem/revisions/${id}`,
    createItem: (edition: string) =>
      `${API_VERSION}/core/pumsem/editions/${edition}/items`,
    /** 장·절 신설(POST, 코드는 서버가 매긴다) / 제목 수정(PUT) / 삭제(DELETE, `?division=&code=`). */
    groups: (edition: string) =>
      `${API_VERSION}/core/pumsem/editions/${edition}/groups`,
    /** 장·절 재정렬(PATCH). 코드를 다시 매기므로 숨긴 판에서만 통한다. */
    groupOrder: (edition: string) =>
      `${API_VERSION}/core/pumsem/editions/${edition}/groups/order`,
  },
  /**
   * 1:1 문의 — core-service 의 inquiry 모듈. 전 경로 인증 필요(Gateway public-paths 미등록).
   * 목록은 본인 문의만(운영자=전체), 답변은 운영자(MANAGER+)만.
   */
  inquiries: {
    list: `${API_VERSION}/core/inquiry`,
    detail: (id: string | number) => `${API_VERSION}/core/inquiry/${id}`,
    create: `${API_VERSION}/core/inquiry`,
    update: (id: string | number) => `${API_VERSION}/core/inquiry/${id}`,
    remove: (id: string | number) => `${API_VERSION}/core/inquiry/${id}`,
    answer: (id: string | number) => `${API_VERSION}/core/inquiry/${id}/answer`,
    markInquiryRead: (id: string | number) => `${API_VERSION}/core/inquiry/${id}/read-answer`,
    userMenuNotification: `${API_VERSION}/core/inquiry/has-unread-answer`,
  },
  /**
   * 협업(파트너십) 문의 — core-service 의 collaboration 모듈.
   * 제출(POST)만 공개다. 조회·상태변경은 admin-web(admin-service) 전용이라 여기 두지 않는다.
   */
  collaboration: {
    create: `${API_VERSION}/core/collaboration`,
  },
  plans: {
    list: `${API_VERSION}/plans`,
    /** 로그인 사용자용 — 신규·장기·지정 고객 플랜까지 판정된 목록 */
    mine: `${API_VERSION}/subscriptions/me/plans`,
  },
  /**
   * 기업 조직 / 라이선스 / 권한 / 구독 / 결제 — billing-service.
   * SSOT 는 `@platform/billing` (console-web 과 공유). 여기서 재정의하지 말 것.
   */
  billing: BILLING_ENDPOINTS,
} as const;
